import t from "@/lang/lang";
import {
  CustomerPageMain,
  CustomerPageMainContent,
  CustomerPageMainHeader,
} from "@/routes/customers/[customerId]/_components/customer-page";
import { CustomerPageBreadcrumbs } from "@/routes/customers/[customerId]/_components/customer-page-breadcrumbs";
import { NavLink, useParams } from "react-router";
import { ServiceObjectName } from "@/services/backend/customers/components/service-object-name";
import { useRelationshipsRelationshipShowQuery } from "@/services/backend/relationships/api/service";
import { formatDate } from "@/shared/lib/utilities/date";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import {
  Commitment,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import { Button } from "@/shared/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { DeleteCommitmentDialog } from "@/routes/customers/[customerId]/relationships/[relationshipId]/commitments/[commitmentId]/_components/delete-commitment-dialog";
import {
  TabsNav,
  TabsNavContent,
  TabsNavLink,
  TabsNavList,
} from "@/shared/components/layout/tabs-nav";

export function CommitmentRoute() {
  const { customerId, relationshipId, commitmentId } = useParams();

  const { data: relationship, error } = useRelationshipsRelationshipShowQuery({
    id: relationshipId!,
  });

  const commitment = relationship?.commitments.find(
    (c) => c.id === commitmentId,
  );

  return (
    <CustomerPageMain>
      <CustomerPageMainHeader>
        <CustomerPageBreadcrumbs
          breadcrumbs={[
            {
              to: `/customers/${customerId}/relationships`,
              label: t("Beziehungen"),
            },
            {
              to: `/customers/${customerId}/relationships/${relationshipId}`,
              label: <ServiceObjectName id={relationship?.serviceObjectId} />,
            },
            {
              to: `/customers/${customerId}/relationships/${relationshipId}/commitments/${commitmentId}`,
              label: commitment
                ? `${formatDate(commitment.date)}, ${commitment.cycle} Wöchentlich`
                : t("Nicht gefunden"),
            },
          ]}
        />
      </CustomerPageMainHeader>
      <CustomerPageMainContent>
        {error && <RTKQueryErrorAlert error={error} />}
        {relationship && !commitment && (
          <Alert variant="destructive">
            <AlertTitle>{t("Nicht gefunden")}</AlertTitle>
            <AlertDescription>
              {t("Es existiert keine Sollstellung mit dieser ID.")}
            </AlertDescription>
          </Alert>
        )}
        {relationship && commitment && (
          <Page relationship={relationship} commitment={commitment} />
        )}
      </CustomerPageMainContent>
    </CustomerPageMain>
  );
}

function Page({
  relationship,
  commitment,
}: {
  relationship: Relationship;
  commitment: Commitment;
}) {
  return (
    <TabsNav>
      <div className="grid gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex gap-2">
            <BackToRelationshipButton relationship={relationship} />
            <TabsNavList>
              <TabsNavLink to="data">{t("Sollstellung")}</TabsNavLink>
              <TabsNavLink to="fulfillments">{t("Arbeitsscheine")}</TabsNavLink>
              <TabsNavLink to="series">{t("Serie")}</TabsNavLink>
            </TabsNavList>
          </div>
          <DeleteCommitmentDialog
            relationship={relationship}
            commitmentId={commitment.id}
          />
        </div>
        <TabsNavContent />
      </div>
    </TabsNav>
  );
}

function BackToRelationshipButton({
  relationship,
}: {
  relationship: Relationship;
}) {
  return (
    <Button asChild variant="outline" className="whitespace-normal">
      <NavLink
        to={`/customers/${relationship.customerId}/relationships/${relationship.id}`}
      >
        <ArrowLeft className="size-4" />
        <span>{t("Zurück zur Beziehung")}</span>
      </NavLink>
    </Button>
  );
}
